import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

export function SortableCategoryHeader({
  id,
  children,
  className,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id,
    data: { type: "category" },
  });

  const transformStr = CSS.Translate.toString(transform);
  const style = {
    transform: isDragging 
      ? `${transformStr || ''} scale(1.02)`.trim()
      : transformStr || undefined,
    transition,
    ...(isDragging ? { zIndex: 50, position: "relative" as const } : {}),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className || ""} ${isOver && !isDragging ? "ring-2 ring-emerald-500 rounded-sm" : ""} ${isDragging ? "shadow-[0_20px_50px_rgba(0,0,0,0.5)] ring-2 ring-emerald-500 bg-zinc-800 opacity-95 rounded-sm" : ""}`}
    >
      <div className="flex items-center w-full min-w-0 gap-1.5">
        <div
          {...attributes}
          {...listeners}
          aria-label="Reorder category"
          className="cursor-move text-zinc-300 hover:text-emerald-400 touch-none pr-1 py-1 hidden md:block shrink-0"
          title="Drag to reorder themes"
        >
          <GripVertical size={14} className="pointer-events-none" />
        </div>
        <div className="flex items-center gap-0.5 md:hidden shrink-0">
          {onMoveUp && (
            <button
              onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
              disabled={isFirst}
              className={`p-1 rounded-sm transition-colors ${
                isFirst ? "text-zinc-600 cursor-not-allowed" : "text-zinc-300 hover:bg-zinc-700/50 hover:text-emerald-400"
              }`}
            >
              <ChevronUp size={14} />
            </button>
          )}
          {onMoveDown && (
            <button
              onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
              disabled={isLast}
              className={`p-1 rounded-sm transition-colors ${
                isLast ? "text-zinc-600 cursor-not-allowed" : "text-zinc-300 hover:bg-zinc-700/50 hover:text-emerald-400"
              }`}
            >
              <ChevronDown size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between flex-1 min-w-0 gap-2">
          {children}
        </div>
      </div>
    </div>
  );
}
